import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Menu,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ButtonActionBar from 'components/ButtonActionBar'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import { useSearchMetrics, useUpdateMetricsVisibility } from 'hooks/useComponents'
import useLocalStorage from 'hooks/useLocalStorage'
import { usePeriods } from 'hooks/usePeriods'
import type { ChangeEvent, FC } from 'react'
import { startTransition, useCallback, useEffect, useState } from 'react'
import type { MetricQueryParams, MetricSearchResponse } from 'types/components'
import { getMetricCompositeId } from 'types/components'
import { createIdLabelMap } from 'types/periods'
import { getOrganizationLabelMap } from 'types/user-profile'
import MetricsTable from '../MetricsTable'
import MetricsVisibilityConfirmationDialog from './MetricsVisibilityConfirmationDialog'

const ManageMetrics: FC = () => {
  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'metrics-selected-organizations',
    []
  )
  const [page, setPage] = useLocalStorage<number>('metrics-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('metrics-rows-per-page', 25, profile?.user?.id)
  const [sort, setSort] = useLocalStorage<string>('metrics-sort', 'label', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('metrics-order', 'asc', profile?.user?.id)
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'metrics-selected-periods',
    [],
    profile?.user?.id
  )
  const [periodLabelMap, setPeriodLabelMap] = useState<{ [key: string]: string }>({})
  const [organizationLabelMap, setOrganizationLabelMap] = useState<{ [key: string]: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [metrics, setMetrics] = useState<MetricSearchResponse>({
    metrics: [],
    paginationInfo: {
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      perPage: 0,
    },
  })
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
  const [isTeamMetrics, setIsTeamMetrics] = useLocalStorage<boolean>('metrics-is-team', true, profile?.user?.id)
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState<null | HTMLElement>(null)
  const [showVisible, setShowVisible] = useLocalStorage<boolean>('metrics-show-visible', true, profile?.user?.id)
  const [showHidden, setShowHidden] = useLocalStorage<boolean>('metrics-show-hidden', false, profile?.user?.id)
  const [showActiveComponents, setShowActiveComponents] = useLocalStorage<boolean>(
    'metrics-show-active-components',
    true,
    profile?.user?.id
  )
  const [showArchivedComponents, setShowArchivedComponents] = useLocalStorage<boolean>(
    'metrics-show-archived-components',
    false,
    profile?.user?.id
  )

  const theme = useTheme()
  const { periods, isLoadingPeriods } = usePeriods(token!, rootOrganizationId, rootOrganizationId !== '')

  // Determine visibility filter based on show toggles
  const getVisibilityFilter = (): boolean | undefined => {
    if (showVisible && !showHidden) return true // Only visible
    if (!showVisible && showHidden) return false // Only hidden
    return undefined // Both or neither - show all
  }

  const queryParams: MetricQueryParams = {
    periodIds: selectedPeriods,
    organizationIds: selectedOrganizations,
    searchTerm,
    pagination: {
      page: page + 1,
      perPage: rowsPerPage,
    },
    sort: [{ field: sort, order }],
    isVisible: getVisibilityFilter(),
    isOrganizationLevel: isTeamMetrics,
    isArchived:
      showActiveComponents && !showArchivedComponents
        ? false
        : !showActiveComponents && showArchivedComponents
          ? true
          : undefined,
  }

  const { metrics: metricsRsp, isLoading: isLoadingMetrics, revalidate } = useSearchMetrics(token!, queryParams)
  const { updateVisibility, isLoading: isUpdatingVisibility } = useUpdateMetricsVisibility(token!)

  useEffect(() => {
    startTransition(() => {
      setIsLoading(isLoadingMetrics || isLoadingPeriods || isLoadingProfile || isUpdatingVisibility)
    })
  }, [isLoadingMetrics, isLoadingPeriods, isLoadingProfile, isUpdatingVisibility])

  useEffect(() => {
    if (metricsRsp) {
      setMetrics(metricsRsp)
    }
  }, [metricsRsp])

  useEffect(() => {
    if (periods) {
      const newPeriodLabelMap =
        periods && periods.periods
          ? periods.periods.reduce((acc: { [key: string]: string }, period) => {
              const idLabelMap = createIdLabelMap(period)
              Object.entries(idLabelMap).forEach(([id, label]) => {
                const newLabel = label.includes(period.label) ? label : `${label} (${period.label})`
                acc[id] = newLabel
              })
              return acc
            }, {})
          : {}

      setPeriodLabelMap(newPeriodLabelMap)
    }
  }, [periods])

  useEffect(() => {
    if (profile) {
      const newOrganizationLabelMap = getOrganizationLabelMap(profile)
      setOrganizationLabelMap(newOrganizationLabelMap)
      // find the first root organization
      const org = profile.roles.find((role) => role.organization.rootOrganizationId != null)
      if (org) {
        setRootOrganizationId(org.organization.rootOrganizationId!)
      }
    }
  }, [profile])

  useEffect(() => {}, [selectedOrganizations, selectedPeriods, searchTerm])

  // Handle sorting
  const handleSort = (property: string) => {
    const isAscending = sort === property && order === 'asc'
    setOrder(isAscending ? 'desc' : 'asc')
    setSort(property)
  }

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handleOrganizationChange = (newOrganizations: string[]) => {
    startTransition(() => {
      setSelectedOrganizations(newOrganizations)
      setPage(0)
    })
  }

  const handlePeriodChange = (newPeriods: string[]) => {
    startTransition(() => {
      setSelectedPeriods(newPeriods)
      setPage(0)
    })
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    startTransition(() => {
      setPage(newPage)
    })
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
    startTransition(() => {
      setRowsPerPage(+event?.target?.value!)
      setPage(0)
    })
  }

  const handleShowVisibleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowVisible(event.target.checked)
    setPage(0)
  }

  const handleShowHiddenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHidden(event.target.checked)
    setPage(0)
  }

  const handleMoreFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreFiltersAnchor(event.currentTarget)
  }

  const handleMoreFiltersClose = () => {
    setMoreFiltersAnchor(null)
  }

  const handleSelectItem = (metricId: string) => {
    setSelectedItems((prev) => (prev.includes(metricId) ? prev.filter((id) => id !== metricId) : [...prev, metricId]))
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(metrics.metrics?.map((metric) => getMetricCompositeId(metric)) || [])
    } else {
      setSelectedItems([])
    }
  }

  const handlePublishSelected = () => {
    setOpenConfirmationDialog(true)
  }

  // Check if all selected items have the same visibility state
  const getSelectedItemsVisibilityState = (): 'mixed' | 'visible' | 'hidden' => {
    const selectedMetrics =
      metrics.metrics?.filter((metric) => selectedItems.includes(getMetricCompositeId(metric))) || []
    if (selectedMetrics.length === 0) return 'mixed'

    const visibilityStates = selectedMetrics.map((metric) => metric.isVisible)
    const allVisible = visibilityStates.every((v) => v === true)
    const allHidden = visibilityStates.every((v) => v === false)

    if (allVisible) return 'visible'
    if (allHidden) return 'hidden'
    return 'mixed'
  }

  const handleConfirmVisibilityChange = async (makeVisible: boolean) => {
    try {
      // Determine the organization ID from the first selected organization or use the root org
      const organizationId = selectedOrganizations.length > 0 ? selectedOrganizations[0] : rootOrganizationId

      if (!organizationId || !rootOrganizationId) {
        console.error('Missing organization ID or root organization ID')
        return
      }

      // Convert selected items (composite string IDs) to MetricDetailsParams objects
      const selectedMetrics =
        metrics.metrics?.filter((metric) => selectedItems.includes(getMetricCompositeId(metric))) || []
      const metricsParams = selectedMetrics.map((metric) => ({
        componentId: metric.componentId,
        organizationId: metric.organizationId,
        periodId: metric.periodId!,
        userId: metric.userId || null,
      }))

      const payload = {
        organizationId,
        rootOrganizationId,
        isVisible: makeVisible,
        metrics: metricsParams,
      }

      console.log('Updating metrics visibility with payload:', payload)

      await updateVisibility(payload)

      // After successful operation, clear selections and close dialog
      setSelectedItems([])
      setOpenConfirmationDialog(false)

      // Trigger data refetch to show updated visibility
      revalidate()
    } catch (error) {
      console.error('Failed to update visibility:', error)
      // You might want to show a user-friendly error message here
    }
  }

  const handleCancelVisibilityChange = () => {
    setOpenConfirmationDialog(false)
  }

  const handleMetricTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsTeamMetrics(event.target.checked)
    setPage(0)
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Actuals" />
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="metric-search-input"
            key="metric-search-input"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={<Switch checked={isTeamMetrics} onChange={handleMetricTypeChange} size="medium" />}
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {isTeamMetrics ? 'Team' : 'Individual'}
                </Typography>
              }
              labelPlacement="start"
              sx={{ margin: 0 }}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 2 }}>
          <MultiPeriodSelector
            periods={periods != null ? periods.periods : []}
            selectedPeriods={selectedPeriods}
            handleChange={handlePeriodChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <MultiOrganizationSelector
            userProfile={profile!}
            selectedOrganizationIds={selectedOrganizations}
            handleChange={handleOrganizationChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleMoreFiltersClick}
              startIcon={<FilterOutlined />}
              sx={{ minWidth: 140 }}
            >
              More Filters
            </Button>
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedOrganizations([])
                  setSelectedPeriods([])
                  setSearchTerm('')
                  setShowVisible(true)
                  setShowHidden(false)
                  setShowActiveComponents(true)
                  setShowArchivedComponents(false)
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <MetricsTable
          periodLabelMap={periodLabelMap}
          organizationLabelMap={organizationLabelMap}
          metrics={metrics}
          page={page}
          rowsPerPage={rowsPerPage}
          sort={sort}
          order={order}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleSort={handleSort}
          selectedItems={selectedItems}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
        />
      )}

      {/* More Filters Menu */}
      <Menu
        anchorEl={moreFiltersAnchor}
        open={Boolean(moreFiltersAnchor)}
        onClose={handleMoreFiltersClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { minWidth: '280px', mt: 1 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            Visibility
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Switch checked={showVisible} onChange={handleShowVisibleChange} size="small" />}
              label="Show Visible"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            <FormControlLabel
              control={<Switch checked={showHidden} onChange={handleShowHiddenChange} size="small" />}
              label="Show Hidden"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            Components
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveComponents}
                  onChange={(e) => {
                    setShowActiveComponents(e.target.checked)
                    setPage(0)
                  }}
                  size="small"
                />
              }
              label="Show Active Components"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showArchivedComponents}
                  onChange={(e) => {
                    setShowArchivedComponents(e.target.checked)
                    setPage(0)
                  }}
                  size="small"
                />
              }
              label="Show Archived Components"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Stack>
        </Box>
      </Menu>

      <MetricsVisibilityConfirmationDialog
        open={openConfirmationDialog}
        onClose={handleCancelVisibilityChange}
        onConfirm={handleConfirmVisibilityChange}
        selectedMetrics={
          metrics.metrics?.filter((metric) => selectedItems.includes(getMetricCompositeId(metric))) || []
        }
        action={getSelectedItemsVisibilityState() === 'visible' ? 'hidden' : 'visible'}
        periodLabelMap={periodLabelMap}
        organizationLabelMap={organizationLabelMap}
      />

      {/* Bottom Action Bar */}
      <ButtonActionBar show={selectedItems.length > 0} maxWidth="900px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseCircleOutlined />}
            onClick={() => setSelectedItems([])}
          >
            Cancel
          </Button>
          {getSelectedItemsVisibilityState() !== 'mixed' && (
            <Button variant="outlined" color="primary" onClick={handlePublishSelected}>
              {getSelectedItemsVisibilityState() === 'visible' ? 'Make Hidden' : 'Make Visible'}
            </Button>
          )}
        </Box>
      </ButtonActionBar>
    </>
  )
}

export default ManageMetrics
